import { Ed25519Keypair } from '@mysten/sui.js/keypairs/ed25519';
import { HDKey } from '@scure/bip32';
import { generateMnemonic, mnemonicToSeedSync, validateMnemonic } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english';
export class SuiWalletGenerator {
    /**
     * Generates a new BIP-39 mnemonic phrase
     * @param wordCount Number of words in the mnemonic (12, 15, 18, 21, or 24)
     * @returns Generated mnemonic phrase
     */
    static generateMnemonic(wordCount = 24) {
        const strength = {
            12: 128,
            15: 160,
            18: 192,
            21: 224,
            24: 256,
        }[wordCount];
        if (!strength) {
            throw new Error(`Invalid word count: ${wordCount}. Must be one of: 12, 15, 18, 21, 24`);
        }
        const mnemonic = generateMnemonic(wordlist, strength);
        // Validate the generated mnemonic
        if (!validateMnemonic(mnemonic, wordlist)) {
            throw new Error('Generated mnemonic failed validation');
        }
        return mnemonic;
    }
    /**
     * Validates a mnemonic phrase
     * @param mnemonic Mnemonic phrase to validate
     * @returns Whether the mnemonic is valid
     */
    static validateMnemonic(mnemonic) {
        return validateMnemonic(mnemonic, wordlist);
    }
    /**
     * Generates a deterministic wallet from a given mnemonic
     * @param mnemonic BIP-39 mnemonic phrase
     * @returns Detailed key information
     */
    static generateWalletFromMnemonic(mnemonic) {
        // Validate mnemonic before processing
        if (!this.validateMnemonic(mnemonic)) {
            throw new Error('Invalid mnemonic phrase');
        }
        // Convert mnemonic to seed
        const seed = mnemonicToSeedSync(mnemonic);
        // Use HDKey for deterministic derivation
        const hdKey = HDKey.fromMasterSeed(seed);
        const derivedKey = hdKey.derive(this.DEFAULT_DERIVATION_PATH);
        // Ensure we have a 32-byte private key for Ed25519
        if (!derivedKey.privateKey || derivedKey.privateKey.length !== 32) {
            throw new Error('Invalid private key for Ed25519');
        }
        const keypair = Ed25519Keypair.fromSecretKey(derivedKey.privateKey);
        return {
            keypair,
            address: keypair.getPublicKey().toSuiAddress(),
            publicKey: keypair.getPublicKey().toBase64(),
            privateKey: derivedKey.privateKey,
            scheme: 'ED25519',
            mnemonic,
        };
    }
}
SuiWalletGenerator.DEFAULT_DERIVATION_PATH = "m/44'/784'/0'/0'/0'";
// Comprehensive test case
function example() {
    try {
        // Generate a mnemonic
        const mnemonic = SuiWalletGenerator.generateMnemonic(24);
        console.log('Generated Mnemonic:', mnemonic);
        // Validate the mnemonic
        console.log('Mnemonic is valid:', SuiWalletGenerator.validateMnemonic(mnemonic));
        // Generate wallet from mnemonic
        const wallet = SuiWalletGenerator.generateWalletFromMnemonic(mnemonic);
        console.log('\nWallet Details:');
        console.log('Address:', wallet.address);
        console.log('Public Key:', wallet.publicKey);
        console.log('Scheme:', wallet.scheme);
    }
    catch (error) {
        console.error('Error:', error);
    }
}
// Uncomment to run
// example();
